import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQuery} from '@github-ui/react-query'
import {Link, useParams} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {Button} from '@primer/react'
import {useEffect} from 'react'

import {renderIssuesSidebar} from '../components/sidebar/render-issues-sidebar'
import {ViewsIndexContent} from '../components/views/ViewsIndexContent'
import {BUTTON_LABELS} from '../constants/buttons'
import {LABELS} from '../constants/labels'
import {
  IssueIndexPage,
  IssueShowPage,
  RepositoryMilestoneIndexPage,
  RepositoryMilestonePage,
  RepositorySavedViewPage,
  RepositoryViewNewPage,
} from '../lazy-routes'
import {savedViewsQueryOptions} from '../queries/saved-views'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import {AnalyticsWrapper} from './AnalyticsWrapper'

export function IssueViewsPage() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  // Deduped with ViewsIndexContent's request via react-query.
  const {data} = useQuery({
    ...savedViewsQueryOptions({owner, repo}),
    enabled: pinnedViewsEnabled && sidebarLayoutEnabled,
  })
  const hasViews = (data?.savedViews?.length ?? 0) > 0
  const viewerCanManageViews = data?.viewerCanManageViews ?? false

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueIndexPage.preload()
      IssueShowPage.preload()
      RepositoryMilestoneIndexPage.preload()
      RepositoryMilestonePage.preload()
      RepositorySavedViewPage.preload()
      RepositoryViewNewPage.preload()
    })
  }, [])

  if (!pinnedViewsEnabled) {
    return null
  }

  const newViewUrl = `/${owner}/${repo}/issues/views/new`
  const headerActions =
    hasViews && viewerCanManageViews ? (
      <Button as={Link} to={newViewUrl} variant="primary">
        {BUTTON_LABELS.newView}
      </Button>
    ) : undefined

  return (
    <RepoIssuesPageLayout
      title={LABELS.viewsTitle}
      dialogTitle={LABELS.viewsTitle}
      actions={headerActions}
      renderSidebar={renderIssuesSidebar}
    >
      <AnalyticsWrapper category="Repository Saved Views Index">
        <ViewsIndexContent newViewUrl={newViewUrl} />
      </AnalyticsWrapper>
    </RepoIssuesPageLayout>
  )
}
